import { useEffect, useEffectEvent, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryState } from 'nuqs';
import { generateCorrelationId } from '@/auth/infrastructure/utils/correlationIdUtils';

interface UseMenuRouteObserverProps {
  moduleParam?: string;
  subModuleParam?: string;
  navigationParams?: Record<string, string>;
}

/**
 * Function that returns the current module and submodule from URL query parameters
 * @returns Object with current module and submodule values
 */
export function getCurrentModule() {
  if (typeof window === 'undefined') {
    return {
      module: undefined,
      submodule: undefined
    };
  }

  const params = new URLSearchParams(window.location.search);
  const module = params.get('module');
  const submodule = params.get('submodule');

  return {
    module: module ?? undefined,
    submodule: submodule ?? undefined
  };
}

/**
 * This hook is used to observe the current module and subModule in the URL.
 * It is used to highlight the correct collapsible item and sub-item in the sidebar.
 * This hook is being used for cases that the user directly accesses a page that has a tabbed interface.
 *
 * For example:
 *
 * /jobs?module=jobs&submodule=table
 * /equipment?module=equipment&submodule=outOfService
 *
 * Or for cases that the user accesses a page that has a tabbed interface, but the page is nested.
 *
 * /jobs/123?module=jobs&submodule=table
 * /equipment/123?module=equipment&submodule=outOfService
 *
 * The "module" and "submodule" nomenclature is used to remove the ambiguity of the "tab" default paramenter from <TabsComponent/>.
 *
 * These `useQueryState` hooks are an easy way to set query params in the URL.
 * For more details, please refer to the nuqs (https://nuqs.47ng.com/) documentation.
 * For more details, please refer to the TabsComponent.tsx documentation.
 *
 * PERFORMANCE: React 19's useEffectEvent for stable URL state updates
 */

export default function useMenuRouteObserver({ moduleParam, subModuleParam }: UseMenuRouteObserverProps) {
  const [, setCurrentModule] = useQueryState('module');
  const [, setCurrentSubModule] = useQueryState('submodule');
  const location = useLocation();
  const lastAppliedRef = useRef<string | null>(null);

  // React 19: useEffectEvent for stable query param updates without re-running effect
  const updateQueryParams = useEffectEvent(() => {
    const params = new URLSearchParams(location.search);
    const urlModule = params.get('module') ?? '';
    const urlSubmodule = params.get('submodule') ?? '';

    const nextKey = `${moduleParam ?? ''}|${subModuleParam ?? ''}`;
    if (lastAppliedRef.current === nextKey) return; // prevent duplicate apply in StrictMode

    let wrote = false;
    if (moduleParam && moduleParam !== urlModule) {
      setCurrentModule(moduleParam);
      wrote = true;
    }
    if (subModuleParam && subModuleParam !== urlSubmodule) {
      setCurrentSubModule(subModuleParam);
      wrote = true;
    }
    if (wrote) {
      lastAppliedRef.current = nextKey;
    }
  });

  useEffect(() => {
    updateQueryParams();

    // Regenerate correlationId when moduleParam or subModuleParam changes
    if (moduleParam || subModuleParam) {
      generateCorrelationId();
    }
  }, [moduleParam, subModuleParam]);
}
